<template>
  <div class="w-full bg-gray-50 h-screen flex flex-col">
    <!-- Header -->
    <div class="bg-white border-b px-4 lg:px-6 py-3 lg:py-4">
      <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div class="flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-4">
          <button @click="goBack" class="text-gray-600 hover:text-gray-800 text-sm lg:text-base">
            <font-awesome-icon icon="arrow-left" class="mr-2" />
            <span class="hidden sm:inline">Zurück zur Tabelle</span>
            <span class="sm:hidden">Zurück</span>
          </button>
          <h1 class="text-lg lg:text-2xl font-bold">
            {{ contractId ? `Vertrag #${contractId} bearbeiten` : 'Neuen Vertrag anlegen' }}
          </h1>
        </div>
        <div class="flex flex-col sm:flex-row gap-2">
          <button v-if="contractId" @click="saveContract" :disabled="loading || !hasChanges"
            class="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-sm lg:text-base text-center sm:text-left">
            <font-awesome-icon icon="save" class="mr-2" />
            Speichern
          </button>
          <button @click="saveAndClose" :disabled="loading"
            class="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 text-sm lg:text-base text-center sm:text-left">
            <font-awesome-icon icon="save" class="mr-2" />
            <span class="hidden sm:inline">Speichern und schließen</span>
            <span class="sm:hidden">Speichern & Schließen</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 overflow-auto p-4 lg:p-6">
      <div class="flex flex-col lg:flex-row gap-4 lg:gap-6">
        <!-- Left table of contents (desktop only) -->
        <nav class="hidden lg:block lg:w-56 xl:w-64 flex-shrink-0" aria-label="Vertragsnavigation">
          <div class="sticky top-4 max-h-[calc(100vh-10rem)] overflow-y-auto rounded-xl border border-gray-200 bg-white p-2 shadow-sm">
            <div class="flex items-center justify-between px-3 pb-2 pt-1">
              <div>
                <div class="text-[11px] font-bold uppercase tracking-wider text-gray-400">Vertrag</div>
                <div class="mt-0.5 text-sm font-semibold text-gray-800">Inhalt</div>
              </div>
              <span class="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">{{ navSections.length }}</span>
            </div>

            <div class="space-y-0.5 border-t border-gray-100 pt-2">
              <template v-for="(item, index) in navSections" :key="item.id">
                <div v-if="item.panel && (index === 0 || !navSections[index - 1].panel)" class="mx-3 my-2 border-t border-gray-100"></div>
                <button
                  @click="scrollToSection(item.id)"
                  class="group flex w-full items-center gap-3 !border-0 !px-3 !py-2 text-left text-sm !font-medium !shadow-none transition-colors duration-150"
                  :class="item.highlight
                    ? '!bg-blue-50 !text-blue-700 hover:!bg-blue-100'
                    : '!bg-transparent !text-gray-600 hover:!bg-gray-50 hover:!text-gray-900'"
                >
                  <span
                    class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg transition-colors"
                    :class="item.highlight ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400 group-hover:bg-gray-200 group-hover:text-gray-600'"
                  >
                    <font-awesome-icon v-if="item.icon" :icon="item.icon" class="text-xs" />
                  </span>
                  <span class="min-w-0 leading-5">{{ item.label }}</span>
                </button>
              </template>
            </div>
          </div>
        </nav>
        <div class="flex-1 min-w-0 grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">

        <!-- Contract Form -->
        <div class="lg:col-span-2">
          <div class="bg-white rounded-lg shadow p-4 lg:p-6" ref="section-stammdaten">
            <h2 class="text-lg lg:text-xl font-semibold mb-4 lg:mb-6 cursor-pointer select-none flex items-center justify-between" @click="toggleSection('stammdaten')">
              <span>Stammdaten</span>
              <font-awesome-icon icon="chevron-down" class="text-base text-gray-500 transition-transform duration-200" :class="{ 'rotate-180': !collapsedSections.stammdaten }" />
            </h2>
            <div v-show="!collapsedSections.stammdaten">

            <!-- Contract ID -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">ID</label>
                <div class="p-2 bg-gray-50 rounded">
                  {{ contractId || 'Wird beim Speichern erzeugt' }}
                </div>
              </div>
            </div>

            <!-- Form Fields -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- Customer Selection -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                  Kunde
                  <font-awesome-icon v-if="customersLoading" icon="spinner" spin class="text-blue-500" />
                </label>
                <select v-model="selectedCustomerId" @change="onCustomerChange" @focus="loadCustomersIfNeeded" class="w-full border rounded px-3 py-2">
                  <option v-if="selectedCustomerId && !customers.find(c => c.id == selectedCustomerId)" :value="selectedCustomerId">
                    {{ currentCustomerLabel }}
                  </option>
                  <option value="">Kunde auswählen</option>
                  <option v-for="customer in customers" :key="customer.id" :value="customer.id">
                    {{ customer.last_name }}, {{ customer.first_name }} ({{ customer.email }})
                  </option>
                </select>
              </div>

              <!-- Project Selection -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Projekt</label>
                <select v-model="selectedProjectId" class="w-full border rounded px-3 py-2">
                  <option value="">Projekt auswählen</option>
                  <option v-for="project in projects" :key="project.id" :value="project.id">
                    {{ project.name }}
                  </option>
                </select>
              </div>

              <!-- Personal Data -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Vorname</label>
                <input v-model="contract.Vorname" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Nachname</label>
                <input v-model="contract.Nachname" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Geschlecht</label>
                <select v-model="contract.Geschlecht" class="w-full border rounded px-3 py-2">
                  <option value="">Bitte auswählen</option>
                  <option value="Männlich">Männlich</option>
                  <option value="Weiblich">Weiblich</option>
                  <option value="Divers">Divers</option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Geburtsdatum</label>
                <input v-model="contract.Geburtsdatum" type="date" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Geburtsort</label>
                <input v-model="contract.Geburtsort" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  Klasse
                  <span v-if="lastKlasseChangeAt" class="font-normal text-gray-500">
                    (zuletzt geändert: {{ formatDateTime(lastKlasseChangeAt) }})
                  </span>
                  <span v-else-if="contract.createdAt" class="font-normal text-gray-500">
                    (angelegt: {{ formatDateTime(contract.createdAt) }})
                  </span>
                </label>
                <input v-model="contract.Klasse" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Schuljahr</label>
                <input v-model="contract.Schuljahr" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select v-model="contract.Status" class="w-full border rounded px-3 py-2">
                  <option value="Eingegangen">Eingegangen</option>
                  <option value="Warteliste">Warteliste</option>
                  <option value="Vorgemerkt">Vorgemerkt</option>
                  <option value="Bestätigt">Bestätigt</option>
                  <option value="Geändert">Geändert</option>
                  <option value="Kündigung eingegangen">Kündigung eingegangen</option>
                  <option value="Gekündigt">Gekündigt</option>
                  <option value="Widerruf eingegangen">Widerruf eingegangen</option>
                  <option value="Widerrufen">Widerrufen</option>
                  <option value="Absage">Absage</option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Unteraccount</label>
                <input v-model="contract.Unteraccount" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Unteraccount Passwort</label>
                <input v-model="contract.UnteraccountPasswort" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Kosten Komplett</label>
                <input v-model="contract.KostenKomplett" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div class="flex items-center">
                <input v-model="contract.Testvertrag" type="checkbox" class="small mr-2" id="testvertrag" />
                <label for="testvertrag" class="text-sm font-medium">Testvertrag</label>
              </div>

              <!-- Contract Dates -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Vertragsbeginn</label>
                <input v-model="contract.Vertragsbeginn" type="date" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Vertragsende</label>
                <input v-model="contract.Vertragsende" type="date" class="w-full border rounded px-3 py-2" />
              </div>

              <!-- Financial -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">{{ (contract.Projekt?.Tagesauswahl || contract.BUTBerechtigt) && !contract.KostenManuellUeberschrieben ? 'Kosten (werden berechnet)' : 'Kosten' }}</label>
                <input v-model="contract.Kosten" type="text" class="w-full border rounded px-3 py-2" :disabled="(contract.BUTBerechtigt || contract.Projekt?.Tagesauswahl) && !contract.KostenManuellUeberschrieben" />
              </div>

              <div class="flex items-center">
                <input v-model="contract.KostenManuellUeberschrieben" type="checkbox" class="small mr-2" id="kostenmanuell" />
                <label for="kostenmanuell" class="text-sm font-medium">Kosten manuell überschrieben</label>
              </div>

              <div class="flex items-center pt-4">
                <input v-model="contract.BUTBerechtigt" type="checkbox" class="small vertical-align-middle" id="but" />
                <label class="text-md font-medium text-gray-700" for="but">BUT berechtigt</label>
              </div>

              <div class="flex items-center">
                <input v-model="contract.BUTDateien" type="checkbox" class="small mr-2" id="butdateien" />
                <label for="butdateien" class="text-sm font-medium">BUT Dateien</label>
              </div>

              <div class="flex items-center">
                <input v-model="contract.BUTNachreichen" type="checkbox" class="small mr-2" id="butnachreichen" />
                <label for="butnachreichen" class="text-sm font-medium">BUT nachreichen</label>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">BUT Art</label>
                <select v-model="contract.BUTArt" class="w-full border rounded px-3 py-2">
                  <option value="">Bitte auswählen</option>
                  <option value="Leistungsbescheid Jobcenter">Leistungsbescheid Jobcenter</option>
                  <option value="Wohngeldbescheid">Wohngeldbescheid</option>
                  <option value="Kinderzuschlag">Kinderzuschlag</option>
                  <option value="Leistungsbescheid nach dem Asylbewerberleistungsgesetz">Leistungsbescheid nach dem
                    Asylbewerberleistungsgesetz</option>
                </select>
              </div>

              <!-- Additional Fields -->
              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-1">Bemerkung</label>
                <textarea v-model="contract.Bemerkung" rows="3" class="w-full border rounded px-3 py-2"></textarea>
              </div>
              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-1">Interner Kommentar</label>
                <textarea v-model="contract.InternerKommentar" rows="3"
                  class="w-full border rounded px-3 py-2 bg-yellow-200"></textarea>
              </div>
            </div>
            </div>

            <!-- Dietary Preferences -->
            <div class="mt-6" ref="section-dietary">
              <h3 class="text-lg font-semibold mb-4 cursor-pointer select-none flex items-center justify-between" @click="toggleSection('dietary')">
                <span>Ernährungspräferenzen</span>
                <font-awesome-icon icon="chevron-down" class="text-base text-gray-500 transition-transform duration-200" :class="{ 'rotate-180': !collapsedSections.dietary }" />
              </h3>
              <div v-show="!collapsedSections.dietary" class="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div class="flex items-center">
                  <input v-model="contract.Vegetarisch" type="checkbox" class="small mr-2" id="vegetarisch" />
                  <label for="vegetarisch" class="text-sm font-medium">Vegetarisch</label>
                </div>
                <div class="flex items-center">
                  <input v-model="contract.Laktosefrei" type="checkbox" class="small mr-2" id="laktosefrei" />
                  <label for="laktosefrei" class="text-sm font-medium">Laktosefrei</label>
                </div>
                <div class="flex items-center">
                  <input v-model="contract.Zoeliakie" type="checkbox" class="small mr-2" id="zoeliakie" />
                  <label for="zoeliakie" class="text-sm font-medium">Zöliakie</label>
                </div>
                <div class="flex items-center">
                  <input v-model="contract.Nussallergie" type="checkbox" class="small mr-2" id="nussallergie" />
                  <label for="nussallergie" class="text-sm font-medium">Nussallergie</label>
                </div>
                <div class="flex items-center">
                  <input v-model="contract.Schweinefleischfrei" type="checkbox" class="small mr-2"
                    id="schweinefleischfrei" />
                  <label for="schweinefleischfrei" class="text-sm font-medium">Schweinefleischfrei</label>
                </div>
                <div class="flex items-center">
                  <input v-model="contract.Sonderessen" type="checkbox" class="small mr-2" id="sonderessen" />
                  <label for="sonderessen" class="text-sm font-medium">Sonderessen</label>
                </div>
                <div class="flex items-center">
                  <input v-model="contract.AllergieNachreichen" type="checkbox" class="small mr-2"
                    id="allergienachreichen" />
                  <label for="allergienachreichen" class="text-sm font-medium">Allergienachweis nachreichen</label>
                </div>
              </div>
            </div>

            <!-- Medical/Special Needs -->
            <div class="mt-6" ref="section-medical">
              <h3 class="text-lg font-semibold mb-4 cursor-pointer select-none flex items-center justify-between" @click="toggleSection('medical')">
                <span>Medizinische Angaben & Förderbedarf</span>
                <font-awesome-icon icon="chevron-down" class="text-base text-gray-500 transition-transform duration-200" :class="{ 'rotate-180': !collapsedSections.medical }" />
              </h3>
              <div v-show="!collapsedSections.medical" class="grid grid-cols-2 gap-4">
                <div class="flex items-center">
                  <input v-model="contract.Behinderung" type="checkbox" class="small mr-2" id="behinderung" />
                  <label for="behinderung" class="text-sm font-medium">Behinderung</label>
                </div>
                <div class="flex items-center">
                  <input v-model="contract.SonderpaedagogischerFoerderbedarf" type="checkbox" class="small mr-2"
                    id="sonderpaedagogischer" />
                  <label for="sonderpaedagogischer" class="text-sm font-medium">Sonderpädagogischer Förderbedarf</label>
                </div>
                <div class="flex items-center">
                  <input v-model="contract.Fluechtlingskind" type="checkbox" class="small mr-2" id="fluechtlingskind" />
                  <label for="fluechtlingskind" class="text-sm font-medium">Flüchtlingskind</label>
                </div>
                <div class="flex items-center">
                  <input v-model="contract.Geschwisterkind" type="checkbox" class="small mr-2" id="geschwisterkind" />
                  <label for="geschwisterkind" class="text-sm font-medium">Geschwisterkind</label>
                </div>
              </div>
            </div>

            <!-- Legal/Consent -->
            <div class="mt-6" ref="section-legal">
              <h3 class="text-lg font-semibold mb-4 cursor-pointer select-none flex items-center justify-between" @click="toggleSection('legal')">
                <span>Einwilligungen & Rechtliches</span>
                <font-awesome-icon icon="chevron-down" class="text-base text-gray-500 transition-transform duration-200" :class="{ 'rotate-180': !collapsedSections.legal }" />
              </h3>
              <div v-show="!collapsedSections.legal" class="grid grid-cols-2 gap-4">
                <div class="flex items-center">
                  <input v-model="contract.AGB" type="checkbox" class="small mr-2" id="agb" />
                  <label for="agb" class="text-sm font-medium">AGB akzeptiert</label>
                </div>
                <div class="flex items-center">
                  <input v-model="contract.Datenschutz" type="checkbox" class="small mr-2" id="datenschutz" />
                  <label for="datenschutz" class="text-sm font-medium">Datenschutz akzeptiert</label>
                </div>
                <div class="flex items-center">
                  <input v-model="contract.Fotoerklaerung" type="checkbox" class="small mr-2" id="fotoerklaerung" />
                  <label for="fotoerklaerung" class="text-sm font-medium">Fotoerklärung</label>
                </div>
                <div class="flex items-center">
                  <input v-model="contract.SEPAMandat" type="checkbox" class="small mr-2" id="sepamandat" />
                  <label for="sepamandat" class="text-sm font-medium">SEPA-Mandat</label>
                </div>
                <div class="flex items-center">
                  <input v-model="contract.Widerruf" type="checkbox" class="small mr-2" id="widerruf" />
                  <label for="widerruf" class="text-sm font-medium">Widerruf</label>
                </div>
                <div class="flex items-center">
                  <input v-model="contract.Selbstzahler" type="checkbox" class="small mr-2" id="selbstzahler" />
                  <label for="selbstzahler" class="text-sm font-medium">Selbstzahler</label>
                </div>
              </div>
            </div>

            <!-- Administrative Fields -->
            <div class="mt-6" ref="section-admin">
              <h3 class="text-lg font-semibold mb-4 cursor-pointer select-none flex items-center justify-between" @click="toggleSection('admin')">
                <span>Administrative Angaben</span>
                <font-awesome-icon icon="chevron-down" class="text-base text-gray-500 transition-transform duration-200" :class="{ 'rotate-180': !collapsedSections.admin }" />
              </h3>
              <div v-show="!collapsedSections.admin" class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    RFID<span v-if="rfidLastUsed" class="font-normal text-gray-500"> (zuletzt genutzt: {{ rfidLastUsed }})<span v-if="rfidLastManual" class="font-bold text-gray-700"> (manual)</span></span>
                  </label>
                  <input v-model="contract.RFID" type="text" class="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">SEPA Mandatsreferenz</label>
                  <input v-model="contract.SEPAMandatsreferenz" type="text" class="w-full border rounded px-3 py-2"
                    readonly />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Betreuungsende</label>
                  <input v-model="contract.Betreuungsende" type="time" class="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">BUT Art</label>
                  <select v-model="contract.BUTArt" class="w-full border rounded px-3 py-2">
                    <option value="">Bitte auswählen</option>
                    <option value="Leistungsbescheid Jobcenter">Leistungsbescheid Jobcenter</option>
                    <option value="Wohngeldbescheid">Wohngeldbescheid</option>
                    <option value="Kinderzuschlag">Kinderzuschlag</option>
                    <option value="Leistungsbescheid nach dem Asylbewerberleistungsgesetz">Leistungsbescheid nach dem
                      Asylbewerberleistungsgesetz</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Kosten Komplett</label>
                  <input v-model="contract.KostenKomplett" type="text" class="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Kündigungszeitpunkt</label>
                  <input v-model="contract.Kuendigungszeitpunkt" type="date" class="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Kündigungskosten</label>
                  <input v-model="contract.Kuendigungskosten" type="text" class="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Geändert zum</label>
                  <input v-model="contract.GeaendertZum" type="text" class="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Eingang Unterlagen</label>
                  <input v-model="contract.EingangUnterlagen" type="date" class="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Zusammengefasst</label>
                  <input v-model="contract.Zusammengefasst" type="text" class="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Bestätigt am</label>
                  <input v-model="contract.BestaetigtAm" type="datetime-local" class="w-full border rounded px-3 py-2"
                    readonly />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Änderung Tage</label>
                  <input v-model="contract.AenderungTage" type="date" class="w-full border rounded px-3 py-2" />
                </div>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div class="flex items-center">
                  <input v-model="contract.AbholungNotwendig" type="checkbox" class="small mr-2"
                    id="abholungnotwendig" />
                  <label for="abholungnotwendig" class="text-sm font-medium">Abholung notwendig</label>
                </div>
                <div class="flex items-center">
                  <input v-model="contract.BUTDateien" type="checkbox" class="small mr-2" id="butdateien-admin" />
                  <label for="butdateien-admin" class="text-sm font-medium">BUT Dateien</label>
                </div>
                <div class="flex items-center">
                  <input v-model="contract.BUTNachreichen" type="checkbox" class="small mr-2" id="butnachreichen-admin" />
                  <label for="butnachreichen-admin" class="text-sm font-medium">BUT nachreichen</label>
                </div>
                <div class="flex items-center">
                  <input v-model="contract.Testvertrag" type="checkbox" class="small mr-2" id="testvertrag-admin" />
                  <label for="testvertrag-admin" class="text-sm font-medium">Testvertrag</label>
                </div>
              </div>
            </div>

            <!-- ID/Document Fields -->
            <div class="mt-6" ref="section-documents">
              <h3 class="text-lg font-semibold mb-4 cursor-pointer select-none flex items-center justify-between" @click="toggleSection('documents')">
                <span>Ausweisdokumente</span>
                <font-awesome-icon icon="chevron-down" class="text-base text-gray-500 transition-transform duration-200" :class="{ 'rotate-180': !collapsedSections.documents }" />
              </h3>
              <div v-show="!collapsedSections.documents" class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Ausweistyp</label>
                  <select v-model="contract.Ausweistyp" class="w-full border rounded px-3 py-2">
                    <option value="">Bitte auswählen</option>
                    <option value="Personalausweis">Personalausweis</option>
                    <option value="Reisepass">Reisepass</option>
                    <option value="Sonstiges">Sonstiges</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Nationalität</label>
                  <input v-model="contract.Nationalitaet" type="text" class="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Ausweisnummer</label>
                  <input v-model="contract.Ausweisnr" type="text" class="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Ausweis gültig bis</label>
                  <input v-model="contract.AusweisGueltigBis" type="date" class="w-full border rounded px-3 py-2" />
                </div>
              </div>
            </div>

            <!-- Free Text Field -->
            <div class="mt-6" ref="section-additional">
              <h3 class="text-lg font-semibold mb-4 cursor-pointer select-none flex items-center justify-between" @click="toggleSection('additional')">
                <span>Zusätzliche Angaben</span>
                <font-awesome-icon icon="chevron-down" class="text-base text-gray-500 transition-transform duration-200" :class="{ 'rotate-180': !collapsedSections.additional }" />
              </h3>
              <div v-show="!collapsedSections.additional">
                <label class="block text-sm font-medium text-gray-700 mb-1">Freifeld</label>
                <input v-model="contract.Freifeld" class="w-full border rounded px-3 py-2" />
              </div>
            </div>

            <!-- Week Days -->
            <div class="mt-6" ref="section-weekdays">
              <h3 class="text-lg font-semibold mb-4 cursor-pointer select-none flex items-center justify-between" @click="toggleSection('weekdays')">
                <span>Wochentage</span>
                <font-awesome-icon icon="chevron-down" class="text-base text-gray-500 transition-transform duration-200" :class="{ 'rotate-180': !collapsedSections.weekdays }" />
              </h3>
              <div v-show="!collapsedSections.weekdays" class="grid grid-cols-5 gap-2 lg:gap-4">
                <div v-for="day in weekDays" :key="day.key" class="text-center">
                  <div class="flex flex-col items-center">
                    <input v-model="contract[day.key]" type="checkbox" class="small" :id="'weekday-' + day.key" />
                    <label :for="'weekday-' + day.key" class="text-md font-medium">{{ day.label }}</label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Day Changes -->
            <div class="mt-6" ref="section-daychanges">
              <h3 class="text-lg font-semibold mb-4 cursor-pointer select-none flex items-center justify-between" @click="toggleSection('daychanges')">
                <span>Tageänderungen</span>
                <font-awesome-icon icon="chevron-down" class="text-base text-gray-500 transition-transform duration-200" :class="{ 'rotate-180': !collapsedSections.daychanges }" />
              </h3>
              <div v-show="!collapsedSections.daychanges">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Änderungen gültig ab</label>
                  <input v-model="contract.AenderungTage" type="date" class="w-full border rounded px-3 py-2" />
                </div>
              </div>
              <div class="grid grid-cols-5 gap-2 lg:gap-4">
                <div v-for="day in weekDays" :key="'aenderung-' + day.key" class="text-center">
                  <div class="flex flex-col items-center">
                    <input v-model="contract['Aenderung' + day.key]" type="checkbox" class="small" :id="'aenderung-' + day.key" />
                    <label :for="'aenderung-' + day.key" class="text-md font-medium">{{ day.label }} (neu)</label>
                  </div>
                </div>
              </div>
              </div>
            </div>

            <!-- Abholzeiten -->
            <div class="mt-6" ref="section-pickup">
              <h3 class="text-lg font-semibold mb-4 cursor-pointer select-none flex items-center justify-between" @click="toggleSection('pickup')">
                <span>Abholzeiten</span>
                <font-awesome-icon icon="chevron-down" class="text-base text-gray-500 transition-transform duration-200" :class="{ 'rotate-180': !collapsedSections.pickup }" />
              </h3>
              <div v-show="!collapsedSections.pickup" class="grid grid-cols-5 gap-2 lg:gap-4">
                <div v-for="day in weekDays" :key="day.key" class="text-center">
                  <label :for="'abholzeit-' + day.key" class="block text-sm font-medium mb-1">{{ day.label }}</label>
                  <input v-model="contract[day.key + 'Abholzeit']" type="time" :id="'abholzeit-' + day.key"
                    class="w-full border rounded px-2 py-1" />
                </div>
              </div>
            </div>

            <!-- Abholzeiten Änderungen -->
            <div class="mt-6" ref="section-pickupChanges">
              <h3 class="text-lg font-semibold mb-4 cursor-pointer select-none flex items-center justify-between" @click="toggleSection('pickupChanges')">
                <span>Abholzeiten Änderungen</span>
                <font-awesome-icon icon="chevron-down" class="text-base text-gray-500 transition-transform duration-200" :class="{ 'rotate-180': !collapsedSections.pickupChanges }" />
              </h3>
              <div v-show="!collapsedSections.pickupChanges">
              <p class="text-sm text-gray-600 mb-4">
                Diese Abholzeiten werden am Datum "Änderungen gültig ab" automatisch übernommen.
              </p>
              <div class="grid grid-cols-5 gap-2 lg:gap-4">
                <div v-for="day in weekDays" :key="'aenderung-abholzeit-' + day.key" class="text-center">
                  <label :for="'aenderung-abholzeit-' + day.key" class="block text-sm font-medium mb-1">{{ day.label }} (neu)</label>
                  <input v-model="contract['Aenderung' + day.key + 'Abholzeit']" type="time" :id="'aenderung-abholzeit-' + day.key"
                    class="w-full border rounded px-2 py-1" />
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Sidebar -->
        <div class="space-y-4 lg:space-y-6">
          <!-- Actions -->
          <div class="bg-white rounded-lg shadow p-4 lg:p-6" v-if="contractId" ref="section-actions">
            <h2 class="text-lg lg:text-xl font-semibold mb-4 cursor-pointer select-none flex items-center justify-between" @click="toggleSection('actions')">
              <span>Aktionen</span>
              <font-awesome-icon icon="chevron-down" class="text-base text-gray-500 transition-transform duration-200" :class="{ 'rotate-180': !collapsedSections.actions }" />
            </h2>
            <div class="space-y-2" v-show="!collapsedSections.actions">
              <button @click="goToCustomer" class="w-full px-3 py-2 border rounded text-left hover:bg-gray-50">
                <font-awesome-icon icon="user" class="mr-2" />
                Zum Kunden
              </button>

              <button v-if="contract.Kunde?.status !== 'Bestätigt'" @click="goToCustomer"
                class="w-full px-3 py-2 border rounded text-left hover:bg-gray-50">
                <font-awesome-icon icon="check" class="mr-2" />
                Kunden bestätigen
              </button>

              <template v-if="canPerformActions">
                <button v-if="originalContract.Status === 'Eingegangen'" @click="addToWaitlist"
                  class="w-full px-3 py-2 border rounded text-left hover:bg-gray-50">
                  <font-awesome-icon icon="list" class="mr-2" />
                  Warteliste (mit Mail)
                </button>

                <button v-if="['Eingegangen', 'Warteliste', 'Geändert', 'Vorgemerkt'].includes(originalContract.Status)"
                  @click="confirmContract(false)" class="w-full px-3 py-2 border rounded text-left hover:bg-gray-50">
                  <font-awesome-icon icon="check-circle" class="mr-2" />
                  Bestätigen (mit Mail)
                </button>

                <button v-if="['Eingegangen', 'Warteliste', 'Geändert', 'Vorgemerkt'].includes(originalContract.Status)"
                  @click="confirmContract(true)" class="w-full px-3 py-2 border rounded text-left hover:bg-gray-50">
                  <font-awesome-icon icon="check-circle" class="mr-2" />
                  Bestätigen (Mail bearbeiten)
                </button>

                <button v-if="['Bestätigt', 'Kündigung eingegangen'].includes(originalContract.Status)"
                  @click="repeatConfirmation" class="w-full px-3 py-2 border rounded text-left hover:bg-gray-50">
                  <font-awesome-icon icon="redo" class="mr-2" />
                  Bestätigen wiederholen
                </button>

                <button v-if="['Bestätigt', 'Kündigung eingegangen'].includes(originalContract.Status)"
                  @click="cancelContract(false)" class="w-full px-3 py-2 border rounded text-left hover:bg-gray-50">
                  <font-awesome-icon icon="times-circle" class="mr-2" />
                  Kündigen (mit Mail)
                </button>

                <button v-if="['Bestätigt', 'Kündigung eingegangen'].includes(originalContract.Status)"
                  @click="cancelContract(true)" class="w-full px-3 py-2 border rounded text-left hover:bg-gray-50">
                  <font-awesome-icon icon="times-circle" class="mr-2" />
                  Kündigen (Mail bearbeiten)
                </button>
                <button v-if="['Widerruf eingegangen'].includes(originalContract.Status)"
                  @click="revokeContract(false)" class="w-full px-3 py-2 border rounded text-left hover:bg-gray-50">
                  <font-awesome-icon icon="times-circle" class="mr-2" />
                  Widerrufen (mit Mail)
                </button>
                <button v-if="['Widerruf eingegangen'].includes(originalContract.Status)"
                  @click="revokeContract(true)" class="w-full px-3 py-2 border rounded text-left hover:bg-gray-50">
                  <font-awesome-icon icon="times-circle" class="mr-2" />
                  Widerrufen (Mail bearbeiten)
                </button>
              </template>

              <button @click="copyContract" class="w-full px-3 py-2 border rounded text-left hover:bg-gray-50">
                <font-awesome-icon icon="copy" class="mr-2" />
                Vertrag kopieren
              </button>

              <button v-if="contract.rfid" @click="openRFID"
                class="w-full px-3 py-2 border rounded text-left hover:bg-gray-50">
                <font-awesome-icon icon="wifi" class="mr-2" />
                RFID Auflegen
              </button>
            </div>
          </div>

          <!-- Documents -->
          <div class="bg-white rounded-lg shadow p-4 lg:p-6" ref="section-documentsPanel">
            <h2 class="text-lg lg:text-xl font-semibold mb-4 cursor-pointer select-none flex items-center justify-between" @click="toggleSection('documentsPanel')">
              <span>Dokumente</span>
              <font-awesome-icon icon="chevron-down" class="text-base text-gray-500 transition-transform duration-200" :class="{ 'rotate-180': !collapsedSections.documentsPanel }" />
            </h2>
            <div v-show="!collapsedSections.documentsPanel">
            <div v-if="documents.length === 0" class="text-gray-500 text-center py-4">
              Keine Dokumente
            </div>
            <div v-else class="space-y-2">
              <div v-for="doc in documents" :key="doc.id" class="border rounded p-3">
                <div class="flex justify-between items-center">
                  <div>
                    <div class="font-medium">{{ doc.originalName }}</div>
                    <div class="text-sm text-gray-500">{{ doc.kind }}</div>
                    <div class="text-xs text-gray-400">{{ formatDate(doc.uploadedAt) }}</div>
                  </div>
                  <a :href="`${baseOrigin}${doc.downloadUrl}`" target="_blank" class="text-blue-600 hover:text-blue-800">
                    <font-awesome-icon icon="download" />
                  </a>
                </div>
              </div>
            </div>
            </div>
          </div>

          <!-- E-Mails -->
          <div class="bg-white rounded-lg shadow p-4 lg:p-6" ref="section-emails">
            <h2 class="text-lg lg:text-xl font-semibold mb-4 cursor-pointer select-none flex items-center justify-between" @click="toggleSection('emails')">
              <span>E-Mails</span>
              <font-awesome-icon icon="chevron-down" class="text-base text-gray-500 transition-transform duration-200" :class="{ 'rotate-180': !collapsedSections.emails }" />
            </h2>
            <div v-show="!collapsedSections.emails">
            <div v-if="sentMails.length === 0" class="text-gray-500 text-center py-4">
              Keine E-Mails gesendet
            </div>
            <div v-else class="space-y-2 max-h-96 overflow-y-auto">
              <div v-for="mail in sentMails" :key="mail.id" class="border rounded p-3">
                <div class="flex justify-between items-center">
                  <div>
                    <div class="font-medium">{{ mail.subject }}</div>
                    <div class="text-sm text-gray-500">
                      <span>Vorlage: {{ mail.templateName }}</span>
                      <span class="mx-2">•</span>
                      <span>{{ mail.toUser }}</span>
                    </div>
                    <div class="text-xs text-gray-400">{{ mail.date }}</div>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>

          <!-- Survey Answers -->
          <div class="bg-white rounded-lg shadow p-4 lg:p-6" v-if="surveyAnswers.length > 0" ref="section-survey">
            <h2 class="text-lg lg:text-xl font-semibold mb-4 cursor-pointer select-none flex items-center justify-between" @click="toggleSection('survey')">
              <span>Umfrage-Antworten</span>
              <font-awesome-icon icon="chevron-down" class="text-base text-gray-500 transition-transform duration-200" :class="{ 'rotate-180': !collapsedSections.survey }" />
            </h2>
            <div class="space-y-2" v-show="!collapsedSections.survey">
              <div v-for="answer in surveyAnswers" :key="answer.id" class="border rounded p-3">
                <div class="font-medium">{{ answer.question }}</div>
                <div class="text-sm text-gray-600">{{ answer.answer }}</div>
                <div class="text-xs text-gray-400">{{ formatDate(answer.createdAt) }}</div>
              </div>
            </div>
          </div>

          <!-- Recent History (Sidebar) -->
          <div class="bg-white rounded-lg shadow p-4 lg:p-6" ref="section-history">
            <h2 class="text-lg lg:text-xl font-semibold mb-4 cursor-pointer select-none flex items-center justify-between" @click="toggleSection('history')">
              <span>Letzte Änderungen</span>
              <font-awesome-icon icon="chevron-down" class="text-base text-gray-500 transition-transform duration-200" :class="{ 'rotate-180': !collapsedSections.history }" />
            </h2>
            <div v-show="!collapsedSections.history">
            <div v-if="meaningfulHistory.length > 0" class="space-y-3 max-h-96 overflow-y-auto">
              <div v-for="(version, index) in meaningfulHistory" :key="version.id" class="border-l-2 border-blue-400 pl-3">
                <div class="text-sm">
                  <div class="font-medium text-gray-900">{{ formatFieldName(version.field) }}</div>
                  <div class="text-gray-600">
                    <span class="line-through">{{ formatValue(version.oldValue, version.field) }}</span>
                    →
                    <span class="text-green-600">{{ formatValue(version.newValue, version.field) }}</span>
                  </div>
                  <div class="text-xs text-gray-500 mt-1">
                    {{ formatDateTime(version.createdAt) }} von {{ version.createdBy }}
                  </div>
                </div>
                <div v-if="index < meaningfulHistory.length - 1" class="border-b border-gray-200 mt-3 pt-3"></div>
              </div>
            </div>
            <div v-else class="text-center text-gray-500 py-8">
              <div class="text-lg">No history</div>
              <div class="text-sm mt-2">Keine Änderungen protokolliert</div>
            </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>

    <!-- Mobile Quick Navigation (floating button + expandable menu) -->
    <div v-if="contractId" class="lg:hidden fixed bottom-4 right-4 z-40">
      <!-- Expandable menu -->
      <div v-if="quickNavOpen" class="absolute bottom-16 right-0 bg-white rounded-lg shadow-xl border w-72 max-h-[70vh] overflow-y-auto">
        <div class="p-3 border-b font-semibold text-gray-700">Schnellnavigation</div>
        <div class="p-2 space-y-1">
          <template v-for="(item, index) in navSections" :key="item.id">
            <div v-if="item.panel && (index === 0 || !navSections[index - 1].panel)" class="border-t my-2"></div>
            <button @click="scrollToSection(item.id)" class="w-full text-left px-3 py-2 rounded text-sm"
              :class="item.highlight ? 'hover:bg-blue-50 font-medium text-blue-700' : 'hover:bg-gray-100'">
              <font-awesome-icon v-if="item.icon" :icon="item.icon" class="mr-2 w-4" :class="item.highlight ? '' : 'text-gray-400'" />
              {{ item.label }}
            </button>
          </template>
        </div>
      </div>
      <!-- Floating button -->
      <button @click="quickNavOpen = !quickNavOpen"
        class="w-14 h-14 rounded-full bg-blue-600 text-white shadow-lg flex items-center justify-center hover:bg-blue-700 transition-colors">
        <font-awesome-icon :icon="quickNavOpen ? 'times' : 'compass'" class="text-xl" />
      </button>
    </div>

    <!-- Loading Overlay -->
    <div v-if="loading" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6">
        <div class="text-lg font-semibold">Lade...</div>
      </div>
    </div>
  </div>
</template>

<script>
import API from '@/API'
import { formatHistoryValue, hasMeaningfulHistoryChange } from '@/lib/history'

export default {
  name: 'ContractEdit',
  props: {
    user: {
      type: Object,
      required: false
    }
  },
  data() {
    return {
      copyMode: false,
      loading: false,
      saving: false,
      contractId: null,
      originalContract: {},
      selectedCustomerId: '',
      selectedProjectId: '',
      currentUser: null,
      contract: {
        id: null,
        Vorname: '',
        Nachname: '',
        Geschlecht: '',
        Geburtsdatum: '',
        Geburtsort: '',
        Klasse: '',
        Schuljahr: '',
        Status: 'Eingegangen',
        Vertragsbeginn: '',
        Vertragsende: '',
        Kosten: '',
        KostenManuellUeberschrieben: false,
        BUTBerechtigt: false,
        Unteraccount: '',
        UnteraccountPasswort: '',
        Bemerkung: '',
        InternerKommentar: '',
        Kunde: null,
        Projekt: null,
        Montag: false,
        Dienstag: false,
        Mittwoch: false,
        Donnerstag: false,
        Freitag: false,
        MontagAbholzeit: '',
        DienstagAbholzeit: '',
        MittwochAbholzeit: '',
        DonnerstagAbholzeit: '',
        FreitagAbholzeit: '',
        AenderungMontag: false,
        AenderungDienstag: false,
        AenderungMittwoch: false,
        AenderungDonnerstag: false,
        AenderungFreitag: false,
        // Dietary fields
        Vegetarisch: false,
        Laktosefrei: false,
        Zoeliakie: false,
        Nussallergie: false,
        Schweinefleischfrei: false,
        Sonderessen: false,
        AllergieNachreichen: false,
        // Medical/Special needs fields
        Behinderung: false,
        SonderpaedagogischerFoerderbedarf: false,
        Fluechtlingskind: false,
        Geschwisterkind: false,
        // Legal/Consent fields
        AGB: false,
        Datenschutz: false,
        Fotoerklaerung: false,
        SEPAMandat: false,
        Widerruf: false,
        // Administrative fields
        RFID: '',
        AbholungNotwendig: false,
        Betreuungsende: '',
        SEPAMandatsreferenz: '',
        Selbstzahler: false,
        BUTDateien: false,
        BUTNachreichen: false,
        BUTArt: '',
        KostenKomplett: '',
        Testvertrag: false,
        Kuendigungszeitpunkt: '',
        Kuendigungskosten: '',
        GeaendertZum: '',
        EingangUnterlagen: '',
        Zusammengefasst: '',
        BestaetigtAm: '',
        AenderungTage: '',
        // ID/Document fields
        Ausweisnr: '',
        AusweisGueltigBis: '',
        Ausweistyp: '',
        Nationalitaet: '',
        // Free text field
        Freifeld: ''
      },
      customers: [],
      customersLoading: false,
      customersLoaded: false,
      projects: [],
      documents: [],
      history: [],
      sentMails: [],
      surveyAnswers: [],
      rfidLastUsed: '',
      rfidLastManual: false,
      quickNavOpen: false,
      collapsedSections: {
        stammdaten: false,
        dietary: true,
        medical: true,
        legal: true,
        admin: true,
        documents: true,
        additional: true,
        weekdays: true,
        daychanges: true,
        pickup: true,
        pickupChanges: true,
        actions: false,
        documentsPanel: false,
        emails: false,
        survey: false,
        history: false
      },
      weekDays: [
        { key: 'Montag', label: 'Mo' },
        { key: 'Dienstag', label: 'Di' },
        { key: 'Mittwoch', label: 'Mi' },
        { key: 'Donnerstag', label: 'Do' },
        { key: 'Freitag', label: 'Fr' }
      ]
    }
  },
  computed: {
    hasChanges() {
      return JSON.stringify(this.contract) !== JSON.stringify(this.originalContract)
    },
    canPerformActions() {
      const user = this.currentUser || this.user
      return user && user.Contracts >= 3
    },
    baseOrigin() {
      try {
        const envUrl = process.env.VUE_APP_INTERNAL_URL
        if (envUrl) {
          return new URL(envUrl).origin
        }
      } catch (e) { }
      return new URL(window.location.href).origin
    },
    currentCustomerLabel() {
      const k = this.contract.Kunde
      if (!k) return ''
      const last = k.last_name || k.NachnameErziehungsberechtigter || ''
      const first = k.first_name || k.VornameErziehungsberechtigter || ''
      const email = k.email || k.EmailEltern || ''
      return `${last}, ${first} (${email})`
    },
    meaningfulHistory() {
      return this.history.filter(version =>
        hasMeaningfulHistoryChange(version.field, version.oldValue, version.newValue)
      )
    },
    lastKlasseChangeAt() {
      return this.history
        .filter(version => version.field === 'Klasse' && version.createdAt)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]?.createdAt || ''
    },
    navSections() {
      const items = [
        { id: 'stammdaten', label: 'Stammdaten', icon: 'edit' },
        { id: 'dietary', label: 'Ernährungspräferenzen', icon: 'leaf' },
        { id: 'medical', label: 'Medizinische Angaben & Förderbedarf', icon: 'user' },
        { id: 'legal', label: 'Einwilligungen & Rechtliches', icon: 'check-circle' },
        { id: 'admin', label: 'Administrative Angaben', icon: 'screwdriver-wrench' },
        { id: 'documents', label: 'Ausweisdokumente', icon: 'file-import' },
        { id: 'additional', label: 'Zusätzliche Angaben', icon: 'file' },
        { id: 'weekdays', label: 'Wochentage', icon: 'calendar' },
        { id: 'daychanges', label: 'Tageänderungen', icon: 'calendar' },
        { id: 'pickup', label: 'Abholzeiten', icon: 'clock' },
        { id: 'pickupChanges', label: 'Abholzeiten Änderungen', icon: 'clock' }
      ]
      if (this.contractId) {
        items.push({ id: 'actions', label: 'Aktionen', icon: 'bolt', panel: true, highlight: true })
      }
      items.push({ id: 'documentsPanel', label: 'Dokumente', icon: 'file', panel: true })
      items.push({ id: 'emails', label: 'E-Mails', icon: 'envelope', panel: true })
      if (this.surveyAnswers.length > 0) {
        items.push({ id: 'survey', label: 'Umfrage-Antworten', icon: 'list', panel: true })
      }
      items.push({ id: 'history', label: 'Letzte Änderungen', icon: 'history', panel: true })
      return items
    }
  },
  watch: {
    selectedCustomerId(newVal) {
      console.log('selectedCustomerId changed:', newVal)
      // Update contract.Kunde when selectedCustomerId changes
      if (!this.customersLoaded || !this.customers.length) {
        // Don't overwrite the Kunde object loaded with the contract before the list is loaded
        return
      }
      if (newVal) {
        this.contract.Kunde = this.customers.find(c => c.id === parseInt(newVal)) || null
        console.log('Updated contract.Kunde:', this.contract.Kunde)
      } else {
        this.contract.Kunde = null
      }
    },
    selectedProjectId(newVal) {
      console.log('selectedProjectId changed:', newVal)
      // Update contract.Projekt when selectedProjectId changes
      if (newVal) {
        this.contract.Projekt = this.projects.find(p => p.id === parseInt(newVal)) || null
        console.log('Updated contract.Projekt:', this.contract.Projekt)
      } else {
        this.contract.Projekt = null
      }
    }
  },
  mounted() {
    // Ensure watchers are triggered after component is mounted
    this.$nextTick(() => {
      if (this.selectedCustomerId && this.customers.length) {
        this.contract.Kunde = this.customers.find(c => c.id === parseInt(this.selectedCustomerId)) || null
      }
      if (this.selectedProjectId && this.projects.length) {
        this.contract.Projekt = this.projects.find(p => p.id === parseInt(this.selectedProjectId)) || null
      }
    })
  },
  methods: {
    toggleSection(section) {
      this.collapsedSections[section] = !this.collapsedSections[section]
    },
    scrollToSection(refName) {
      if (refName in this.collapsedSections) {
        this.collapsedSections[refName] = false
      }
      this.$nextTick(() => {
        const ref = this.$refs['section-' + refName]
        const el = Array.isArray(ref) ? ref[0] : ref
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
      this.quickNavOpen = false
    },
    async loadContract() {
      if (!this.contractId) return

      this.loading = true
      try {
        const response = await new Promise((resolve, reject) => {
          API.getContract(this.contractId, (data, status) => {
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error(`Failed to load contract: ${status}`))
            }
          })
        })

        // Convert date formats from backend to frontend
        const processedResponse = this.convertDatesFromBackend(response)

        this.contract = { ...this.contract, ...processedResponse }
        if (this.$route.query.copy !== undefined) {
          this.contractId = false
          delete this.contract.id
        }
        this.originalContract = JSON.parse(JSON.stringify(this.contract))

        console.log('Contract loaded:', this.contract)
        console.log('Contract Kunde:', this.contract.Kunde)
        console.log('Contract Projekt:', this.contract.Projekt)

        // Set selected IDs for dropdowns
        this.selectedCustomerId = this.contract.Kunde?.id?.toString() || ''
        this.selectedProjectId = this.contract.Projekt?.id?.toString() || ''

        console.log('Selected Customer ID:', this.selectedCustomerId)
        console.log('Selected Project ID:', this.selectedProjectId)

        this.documents = response.documents || []
        this.surveyAnswers = response.surveyAnswers || []

        // Load sent mails
        if (this.contractId) {
          await this.loadSentMails()
        }

        // History is now loaded separately via dedicated endpoint
        if (this.contractId) {
          await this.reloadHistory()
        }

        // Load RFID last used info
        if (this.contractId) {
          this.loadRFIDLastUsed()
        }

      } catch (error) {
        console.error('Error loading contract:', error)
        this.$toast?.open?.({
          message: 'Fehler beim Laden des Vertrags',
          type: 'error',
          position: 'top'
        })
      } finally {
        this.loading = false
      }
    },
    loadSentMails() {
      API.getContractSentMails(this.contractId, (res, status) => {
        if (status === 200) {
          this.sentMails = res.emails || []
        }
      })
    },
    loadRFIDLastUsed() {
      API.getContractLastPickup(this.contractId, (res, status) => {
        if (status === 200 && res.lastUsed) {
          this.rfidLastUsed = res.lastUsed
          this.rfidLastManual = res.manual === true
        }
      })
    },

    async loadCustomersIfNeeded() {
      if (this.customersLoaded || this.customersLoading) {
        return
      }
      await this.loadCustomers()
    },

    async loadCustomers() {
      this.customersLoading = true
      try {
        console.log('Loading customers...')
        const customers = await new Promise((resolve, reject) => {
          API.getCustomers((data, status) => {
            console.log('Customers API response:', { data, status })
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error('Failed to load customers'))
            }
          })
        })
        this.customers = customers
        this.customersLoaded = true
        console.log('Customers loaded:', customers.length)

        // Restore full Kunde object from the loaded list when possible
        if (this.selectedCustomerId) {
          this.contract.Kunde = this.customers.find(c => c.id === parseInt(this.selectedCustomerId)) || null
          console.log('Updated contract.Kunde from loaded customers:', this.contract.Kunde)
        }
      } catch (error) {
        console.error('Error loading customers:', error)
      } finally {
        this.customersLoading = false
      }
    },

    async loadProjects() {
      try {
        console.log('Loading projects...')
        const projects = await new Promise((resolve, reject) => {
          API.getProjects((data, status) => {
            console.log('Projects API response:', { data, status })
            console.log('First project sample:', data[0])
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error('Failed to load projects'))
            }
          })
        })
        this.projects = projects
        console.log('Projects loaded:', projects.length)
        console.log('Project fields available:', projects.length > 0 ? Object.keys(projects[0]) : 'No projects')
      } catch (error) {
        console.error('Error loading projects:', error)
      }
    },

    async saveContract() {
      this.saving = true
      try {
        // Prepare contract data for API - convert empty date strings to null
        const contractData = { ...this.contract }

        // List of date fields that should be null instead of empty strings
        const dateFields = [
          'Geburtsdatum', 'Vertragsbeginn', 'Vertragsende', 'Kuendigungszeitpunkt',
          'EingangUnterlagen', 'AenderungTage', 'AusweisGueltigBis'
        ]

        // List of time fields that should be null instead of empty strings
        const timeFields = [
          'Betreuungsende', 'MontagAbholzeit', 'DienstagAbholzeit',
          'MittwochAbholzeit', 'DonnerstagAbholzeit', 'FreitagAbholzeit',
          'AenderungMontagAbholzeit', 'AenderungDienstagAbholzeit',
          'AenderungMittwochAbholzeit', 'AenderungDonnerstagAbholzeit', 'AenderungFreitagAbholzeit'
        ]

        // Convert empty date strings to null
        dateFields.forEach(field => {
          if (contractData[field] === '') {
            contractData[field] = null
          }
        })

        // Convert empty time strings to null
        timeFields.forEach(field => {
          if (contractData[field] === '') {
            contractData[field] = null
          }
        })

        const response = await new Promise((resolve, reject) => {
          if (this.contractId) {
            API.updateContract(this.contractId, contractData, (data, status) => {
              if (status === 200) {
                resolve(data)
              } else {
                reject(new Error('Failed to update contract'))
              }
            })
          } else {
            API.createContract(contractData, (data, status) => {
              if (status === 201) {
                resolve(data)
              } else {
                reject(new Error('Failed to create contract'))
              }
            })
          }
        })

        // Convert dates from backend response before updating contract
        const processedResponse = this.convertDatesFromBackend(response)
        this.contract = { ...this.contract, ...processedResponse }
        this.originalContract = JSON.parse(JSON.stringify(this.contract))

        if (!this.contractId) {
          this.contractId = response.id
          this.$router.replace({ query: { ...this.$route.query, id: this.contractId } })
          await this.loadContract()
        } else {
          // For existing contracts, reload the history to show the new change
          await this.reloadHistory()
        }

        this.$toast?.open?.({
          message: 'Vertrag gespeichert',
          type: 'success',
          position: 'top'
        })
        return true

      } catch (error) {
        console.error('Error saving contract:', error)
        this.$toast?.open?.({
          message: 'Fehler beim Speichern',
          type: 'error',
          position: 'top'
        })
      } finally {
        this.saving = false
      }
      return false;
    },

    async saveAndClose() {
      if (await this.saveContract()) {
        this.goBack()
      }
    },

    async performAction(action, params = {}) {
      try {
        const response = await new Promise((resolve, reject) => {
          API.performContractAction(this.contractId, { action, ...params }, (data, status) => {
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error('Failed to perform action'))
            }
          })
        })

        // If mailto link is returned, open it in new window
        if (response.mailto) {
          window.open(response.mailto, '_blank')
        }

        this.contract = { ...this.contract, ...response.contract }
        this.originalContract = JSON.parse(JSON.stringify(this.contract))

        this.$toast?.open?.({
          message: response.message || 'Aktion erfolgreich',
          type: 'success',
          position: 'top'
        })

      } catch (error) {
        console.error('Error performing action:', error)
        this.$toast?.open?.({
          message: 'Fehler bei der Aktion',
          type: 'error',
          position: 'top'
        })
      }
    },

    goToCustomer() {
      if (this.contract.Kunde?.id) {
        localStorage.setItem("confirmOrigin",window.location)
        window.location = `/customers/${this.contract.Kunde.id}`
      }
    },


    async confirmContract(editMail = false) {
      await this.performAction('confirm', { editMail })
    },

    async addToWaitlist() {
      await this.performAction('waitlist')
    },

    async repeatConfirmation() {
      await this.performAction('confirm')
    },

    async cancelContract(editMail = false) {
      if (confirm('Möchten Sie diesen Vertrag wirklich kündigen?')) {
        await this.performAction('cancel', { editMail })
      }
    },
    async revokeContract(editMail = false) {
      if (confirm('Möchten Sie diesen Vertrag wirklich widerrufen?')) {
        await this.performAction('revoke', { editMail })
      }
    },

    copyContract() {
      window.location += "?copy"
    },

    openRFID() {
      if (!this.contract.Projekt?.Titel || !this.contract.rfid) return

      let url = ''
      const projectTitle = this.contract.Projekt.Titel

      if ('Erft' in projectTitle) {
        url = `https://mensaerft.ev-angel-isch.de/?rfid=${this.contract.rfid}`
      } else if ('Monte' in projectTitle) {
        url = `https://mensamonte.ev-angel-isch.de/?rfid=${this.contract.rfid}`
      } else if ('Gutenberg' in projectTitle) {
        url = `https://mensagugy.ev-angel-isch.de/?rfid=${this.contract.rfid}`
      } else if ('Gesamtschule' in projectTitle) {
        url = `https://mein.ev-angel-isch.de/mensa/gesamtbergheim?rfid=${this.contract.rfid}`
      } else if ('Erich' in projectTitle) {
        url = `https://mein.ev-angel-isch.de/mensa/erichkaestner?rfid=${this.contract.rfid}`
      } else if ('Albert' in projectTitle) {
        url = `https://mein.ev-angel-isch.de/mensa/alberteinstein?rfid=${this.contract.rfid}`
      }

      if (url) {
        window.open(url, '_blank')
      }
    },

    goBack() {
      // Get the saved table referrer from cookie (same mechanism as table.html)
      const getCookie = (name) => {
        const value = `; ${document.cookie}`
        const parts = value.split(`; ${name}=`)
        if (parts.length === 2) return parts.pop().split(';').shift()
        return null
      }

      const tableReferrer = getCookie('tableReferrer')
      const destination = tableReferrer || document.referrer || '/intern/contracts/'

      console.log('Redirecting back to:', destination)
      window.location = destination
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString('de-DE')
    },

    formatDateTime(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    formatValue(value, fieldName) {
      if (value === true) return 'Ja'
      if (value === false) return 'Nein'
      if (value === null || value === undefined) return ''
      return formatHistoryValue(fieldName, value)
    },

    formatFieldName(fieldName) {
      const fieldTranslations = {
        'Vorname': 'Vorname',
        'Nachname': 'Nachname',
        'Geschlecht': 'Geschlecht',
        'Geburtsdatum': 'Geburtsdatum',
        'Geburtsort': 'Geburtsort',
        'Klasse': 'Klasse',
        'Schuljahr': 'Schuljahr',
        'Status': 'Status',
        'Vertragsbeginn': 'Vertragsbeginn',
        'Vertragsende': 'Vertragsende',
        'Kosten': 'Kosten',
        'KostenManuellUeberschrieben': 'Kosten manuell überschrieben',
        'BUTBerechtigt': 'BUT Berechtigt',
        'Bemerkung': 'Bemerkung',
        'Montag': 'Montag',
        'Dienstag': 'Dienstag',
        'Mittwoch': 'Mittwoch',
        'Donnerstag': 'Donnerstag',
        'Freitag': 'Freitag',
        'Vegetarisch': 'Vegetarisch',
        'Laktosefrei': 'Laktosefrei',
        'Zoeliakie': 'Zöliakie',
        'Nussallergie': 'Nussallergie',
        'Schweinefleischfrei': 'Schweinefleischfrei',
        'Sonderessen': 'Sonderessen',
        'AllergieNachreichen': 'Allergie Nachreichen',
        'Behinderung': 'Behinderung',
        'SonderpaedagogischerFoerderbedarf': 'Sonderpädagogischer Förderbedarf',
        'Fluechtlingskind': 'Flüchtlingskind',
        'Geschwisterkind': 'Geschwisterkind',
        'AGB': 'AGB',
        'Datenschutz': 'Datenschutz',
        'Fotoerklaerung': 'Fotoerklärung',
        'SEPAMandat': 'SEPA Mandat',
        'Widerruf': 'Widerruf',
        'RFID': 'RFID',
        'AbholungNotwendig': 'Abholung notwendig',
        'Betreuungsende': 'Betreuungsende',
        'SEPAMandatsreferenz': 'SEPA Mandatsreferenz',
        'Selbstzahler': 'Selbstzahler',
        'BUTDateien': 'BUT Dateien',
        'BUTNachreichen': 'BUT nachreichen',
        'BUTArt': 'BUT Art',
        'KostenKomplett': 'Kosten Komplett',
        'Testvertrag': 'Testvertrag',
        'Kuendigungszeitpunkt': 'Kündigungszeitpunkt',
        'Kuendigungskosten': 'Kündigungskosten',
        'GeaendertZum': 'Geändert zum',
        'EingangUnterlagen': 'Eingang Unterlagen',
        'Zusammengefasst': 'Zusammengefasst',
        'BestaetigtAm': 'Bestätigt am',
        'AenderungTage': 'Änderung Tage',
        'Ausweisnr': 'Ausweisnummer',
        'AusweisGueltigBis': 'Ausweis gültig bis',
        'Ausweistyp': 'Ausweistyp',
        'Nationalitaet': 'Nationalität',
        'Freifeld': 'Freifeld',
        'Kunde': 'Kunde',
        'Projekt': 'Projekt'
      }

      return fieldTranslations[fieldName] || fieldName
    },

    convertDatesFromBackend(data) {
      const processedData = { ...data }

      // List of date fields that need conversion from ISO to YYYY-MM-DD
      const dateFields = [
        'Geburtsdatum', 'Vertragsbeginn', 'Vertragsende', 'Kuendigungszeitpunkt',
        'EingangUnterlagen', 'AenderungTage', 'AusweisGueltigBis', 'VorzeitigeKuendigung'
      ]

      // List of datetime fields that need conversion from ISO to datetime-local format
      const datetimeFields = [
        'BestaetigtAm', 'createdAt', 'updatedAt'
      ]

      // Time fields should already be in HH:MM format from backend
      const timeFields = [
        'Betreuungsende', 'MontagAbholzeit', 'DienstagAbholzeit',
        'MittwochAbholzeit', 'DonnerstagAbholzeit', 'FreitagAbholzeit'
      ]

      // Convert date fields
      dateFields.forEach(field => {
        if (processedData[field]) {
          try {
            const date = new Date(processedData[field])
            processedData[field] = date.toISOString().split('T')[0] // YYYY-MM-DD
          } catch (e) {
            console.warn(`Failed to convert date field ${field}:`, processedData[field])
            processedData[field] = ''
          }
        }
      })

      // Convert datetime fields
      datetimeFields.forEach(field => {
        if (processedData[field]) {
          try {
            const date = new Date(processedData[field])
            // Convert to local datetime-local format (YYYY-MM-DDTHH:MM)
            const offset = date.getTimezoneOffset()
            const localDate = new Date(date.getTime() - (offset * 60000))
            processedData[field] = localDate.toISOString().slice(0, 16)
          } catch (e) {
            console.warn(`Failed to convert datetime field ${field}:`, processedData[field])
            processedData[field] = ''
          }
        }
      })

      // Time fields should already be in correct format, but ensure they're not null
      timeFields.forEach(field => {
        if (processedData[field] === null) {
          processedData[field] = ''
        }
      })

      return processedData
    },
    async reloadHistory() {
      console.log('Starting history reload for contract:', this.contractId)

      // Add a short delay to ensure the backend has processed the change
      await new Promise(resolve => setTimeout(resolve, 500))

      console.log('Delay completed, calling API.getContractHistory')

      try {
        const response = await new Promise((resolve, reject) => {
          console.log('Making API call to getContractHistory')
          API.getContractHistory(this.contractId, (data, status) => {
            console.log('API.getContractHistory response:', { data, status })
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error(`Failed to load history: ${status}`))
            }
          })
        })

        console.log('History API response:', response)
        this.history = response || []
        console.log('History reloaded successfully:', this.history)

      } catch (error) {
        console.error('Error reloading history:', error)
      }
    },

    async fetchUserData() {
      try {
        const response = await new Promise((resolve, reject) => {
          API.checkAuth((data, status) => {
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error('Failed to fetch user data'))
            }
          })
        })
        this.currentUser = response
      } catch (error) {
        console.error('Error fetching user data:', error)
        // Redirect to login if user data cannot be fetched
        window.location.href = `${process.env.VUE_APP_LOGIN_URL}?next=${window.location.href}`
      }
    },
    onCustomerChange() {
      // Auto-fill customer data if contract is new
      if (!this.contractId && this.selectedCustomerId) {
        const customer = this.customers.find(c => c.id === parseInt(this.selectedCustomerId))
        if (customer) {
          this.contract.Vorname = customer.first_name || ''
          this.contract.Nachname = customer.last_name || ''
        }
      }
    }
  },

  async created() {
    this.contractId = parseInt(this.$route.params.id) || null

    // Fetch user data if not provided as prop
    if (!this.user) {
      await this.fetchUserData()
    }

    await this.loadProjects()
    if (this.contractId) {
      await this.loadContract()
    }

  }
}
</script>

<style scoped>
/* Custom checkbox styling */
input[type="checkbox"] {
  appearance: none;
  background-color: #fff;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
}

input[type="checkbox"]:checked {
  background-color: #3b82f6;
  border-color: #3b82f6;
}

input[type="checkbox"]:checked::before {
  content: '';
  position: absolute;
  display: block;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background-color: white;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

input[type="checkbox"]:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

input[type="checkbox"]:hover {
  border-color: #9ca3af;
}

input[type="checkbox"]:checked:hover {
  background-color: #2563eb;
  border-color: #2563eb;
}

/* Hide native clock icon on time inputs to prevent overlay on narrow mobile columns */
@media (max-width: 1023px) {
  input[type="time"]::-webkit-calendar-picker-indicator {
    display: none;
  }
  input[type="time"] {
    -webkit-appearance: none;
    -moz-appearance: textfield;
    padding-left: 4px;
    padding-right: 4px;
  }
}
</style>
